import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../auth/useApi';

/* ============================================================================
 * DescriptionBody — renders ticket/comment text that may embed images.
 *
 * Images are stored as `![alt](kbimg:<blob>)` (private blob) or `![alt](https://…)`
 * (external). The storage account is private, so a tokenless <img> can't load the
 * raw blob URL — for each `kbimg:` ref this fetches a short-lived SAS from
 * GET /api/kb/image-sas and renders <img> against that signed URL. No link shown,
 * just the image. Everything else renders as pre-wrapped text. Only kbimg refs and
 * http(s) URLs become images, so stored text can't inject arbitrary markup.
 * ========================================================================== */

const TOKEN_RE = /!\[([^\]]*)\]\((kbimg:[A-Za-z0-9.\-_]+|https?:\/\/[^\s)]+)\)/g;

export function DescriptionBody({ text, style }: { text: string | null | undefined; style?: React.CSSProperties }) {
  const api = useApi();
  const [sas, setSas] = useState<Record<string, string>>({});

  // distinct private-blob names referenced in this text
  const blobs = useMemo(() => {
    if (!text) return [] as string[];
    const set = new Set<string>();
    let m: RegExpExecArray | null;
    TOKEN_RE.lastIndex = 0;
    while ((m = TOKEN_RE.exec(text)) !== null) {
      if (m[2].startsWith('kbimg:')) set.add(m[2].slice('kbimg:'.length));
    }
    return Array.from(set);
  }, [text]);

  const blobsKey = blobs.join('|');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const blob of blobs) {
        try {
          const { url } = await api.get<{ url: string }>(`/api/kb/image-sas?blob=${encodeURIComponent(blob)}`);
          if (!cancelled) setSas(prev => (prev[blob] ? prev : { ...prev, [blob]: url }));
        } catch {
          /* leave unresolved → shows a placeholder rather than a broken image */
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blobsKey]);

  if (!text) return null;

  const imgStyle: React.CSSProperties = {
    display: 'block', maxWidth: '100%', borderRadius: 6, margin: '8px 0', border: '1px solid var(--c-border)',
  };
  const phStyle: React.CSSProperties = {
    display: 'inline-block', padding: '8px 12px', margin: '8px 0', borderRadius: 6,
    border: '1px dashed var(--c-border)', color: 'var(--c-textMuted)', fontSize: 12, fontStyle: 'italic',
  };

  const nodes: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(text)) !== null) {
    if (m.index > last) {
      nodes.push(<span key={key++} style={{ whiteSpace: 'pre-wrap' }}>{text.slice(last, m.index)}</span>);
    }
    const alt = m[1] || 'image';
    const target = m[2];
    if (target.startsWith('kbimg:')) {
      const blob = target.slice('kbimg:'.length);
      const url = sas[blob];
      nodes.push(url
        ? <img key={key++} src={url} alt={alt} style={imgStyle} />
        : <span key={key++} style={phStyle}>Loading image…</span>);
    } else {
      nodes.push(<img key={key++} src={target} alt={alt} style={imgStyle} />);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    nodes.push(<span key={key++} style={{ whiteSpace: 'pre-wrap' }}>{text.slice(last)}</span>);
  }

  return <div style={{ lineHeight: 1.6, color: 'var(--c-text)', ...style }}>{nodes}</div>;
}
